import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { findBySlug } from '../config/catalogue';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import {
  ArrowLeft,
  ShoppingCart,
  Heart,
  FileText,
  BookOpen,
  Download,
  ShieldCheck,
  Tag,
  LogIn,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const ProductDetail = () => {
  const { category, product: productSlug } = useParams();
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === 'true';
  const { isLoggedIn, login } = useAuth();
  const { addToCart, toggleWishlist, isWishlisted } = useCart();
  const [activeSlide, setActiveSlide] = useState(0);
  const [detailsMd, setDetailsMd] = useState('');

  useEffect(() => {
    setActiveSlide(0);
    setDetailsMd('');
  }, [category, productSlug]);

  // Login gate
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-page">
        {!isEmbedded && <Header />}
        <div className={`flex items-center justify-center ${isEmbedded ? 'min-h-screen' : 'min-h-[calc(100vh-73px)]'}`}>
          <div className="text-center space-y-6 max-w-md px-6">
            <div className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center" style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)' }}>
              <LogIn size={36} style={{ color: '#F97316' }} />
            </div>
            <h1 className="text-3xl font-bold text-foreground-primary">Sign in Required</h1>
            <p className="text-foreground-secondary">Log in to view product details.</p>
            <button
              onClick={login}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-smooth"
              style={{ background: '#F97316', color: '#0B0E13' }}
            >
              <LogIn size={18} />
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  const item = findBySlug(category, productSlug);

  if (!item) {
    return (
      <div className="min-h-screen bg-page">
        {!isEmbedded && <Header />}
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-73px)] space-y-4">
          <p className="text-xl font-bold text-foreground-primary">Product not found</p>
          <Link to="/marketplace" className="text-sm text-foreground-muted hover:text-foreground-primary transition-smooth flex items-center gap-2">
            <ArrowLeft size={14} /> Back to Marketplace
          </Link>
        </div>
      </div>
    );
  }

  const isFree = item.price === 0;
  const wishlisted = isWishlisted(item.id);
  const photos = item.photos || [item.photo];

  return (
    <div className="min-h-screen bg-page">
      {!isEmbedded && <Header />}
      <div className="max-w-5xl mx-auto px-6" style={isEmbedded ? { paddingTop: '2rem' } : { paddingTop: 'calc(73px + 2rem)' }}>
        {/* Back link + Breadcrumb */}
        <Link
          to="/marketplace"
          className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground-primary transition-smooth mb-4"
        >
          <ArrowLeft size={14} /> Back to Marketplace
        </Link>
        <nav className="flex items-center gap-2 text-sm text-foreground-muted mb-8">
          <Link to="/marketplace" className="hover:text-foreground-primary transition-smooth">Marketplace</Link>
          <span>/</span>
          <Link to={`/marketplace?category=${encodeURIComponent(item.category)}`} className="hover:text-foreground-primary transition-smooth">{item.category}</Link>
          <span>/</span>
          <span className="text-foreground-primary">{item.name}</span>
        </nav>

        <div className="grid md:grid-cols-2 gap-10">
          {/* Image Slideshow */}
          <div className="space-y-3">
            <div
              className="relative aspect-square rounded-2xl overflow-hidden"
              style={{ background: 'rgba(249,115,22,0.04)', border: '1px solid rgba(249,115,22,0.1)' }}
            >
              <img
                src={photos[activeSlide]}
                alt={`${item.name} — ${activeSlide + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {photos.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveSlide((prev) => (prev - 1 + photos.length) % photos.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-smooth"
                    style={{ background: 'rgba(0,0,0,0.5)', color: '#fff' }}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={() => setActiveSlide((prev) => (prev + 1) % photos.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-smooth"
                    style={{ background: 'rgba(0,0,0,0.5)', color: '#fff' }}
                  >
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
            </div>
            {photos.length > 1 && (
              <ThumbnailStrip photos={photos} activeSlide={activeSlide} setActiveSlide={setActiveSlide} itemName={item.name} />
            )}
          </div>

          {/* Details */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                  style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316' }}
                >
                  {item.badge}
                </span>
                <span className="text-xs text-foreground-muted flex items-center gap-1">
                  <Tag size={10} /> {item.category}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-foreground-primary mb-3">{item.name}</h1>
              <p className="text-foreground-secondary leading-relaxed">{item.description}</p>
            </div>

            {/* Price */}
            <div className="glass-frosted rounded-xl p-5 glow-subtle">
              <div className="flex items-center justify-between mb-4">
                <span className="text-3xl font-bold" style={{ color: isFree ? '#22c55e' : '#F97316' }}>
                  {isFree ? 'Free' : `${item.currency}${item.price.toLocaleString()}`}
                </span>
                {item.badge === 'Forge Certified' && (
                  <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#22c55e' }}>
                    <ShieldCheck size={14} /> Verified
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => addToCart(item)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-smooth"
                  style={{ background: '#F97316', color: '#0B0E13' }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 30px rgba(249,115,22,0.5)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {isFree ? <Download size={18} /> : <ShoppingCart size={18} />}
                  {isFree ? 'Get for Free' : 'Add to Cart'}
                </button>
                <button
                  onClick={() => toggleWishlist(item)}
                  className="p-3 rounded-lg transition-smooth border border-subtle hover:bg-subtle"
                  title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                >
                  <Heart size={18} fill={wishlisted ? '#F97316' : 'none'} style={{ color: wishlisted ? '#F97316' : 'var(--fg-muted)' }} />
                </button>
              </div>
            </div>

            {/* Downloads */}
            {(item.datasheet || item.manual) && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground-primary">Resources</h3>
                <div className="flex flex-wrap gap-3">
                  {item.datasheet && (
                    <a
                      href={item.datasheet}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth text-foreground-secondary border border-subtle hover:bg-subtle hover:text-foreground-primary"
                    >
                      <FileText size={14} /> Datasheet
                    </a>
                  )}
                  {item.manual && (
                    <a
                      href={item.manual}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth text-foreground-secondary border border-subtle hover:bg-subtle hover:text-foreground-primary"
                    >
                      <BookOpen size={14} /> Manual
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="space-y-2 pt-2 border-t border-subtle">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Product ID</span>
                <span className="text-foreground-secondary font-mono text-xs">{item.id}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Category</span>
                <span className="text-foreground-secondary">{item.category}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Publisher</span>
                <span className="text-foreground-secondary">{item.badge}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details (from markdown) */}
        <DetailSection detailsFile={item.detailsFile} detailsMd={detailsMd} setDetailsMd={setDetailsMd} />

        <div className="pb-12" />
      </div>
    </div>
  );
};

const VISIBLE_THUMBS = 4;

const ThumbnailStrip = ({ photos, activeSlide, setActiveSlide, itemName }) => {
  const maxStart = Math.max(0, photos.length - VISIBLE_THUMBS);
  const thumbStart = Math.max(0, Math.min(activeSlide, maxStart));
  const visiblePhotos = photos.slice(thumbStart, thumbStart + VISIBLE_THUMBS);
  const canScrollLeft = thumbStart > 0;
  const canScrollRight = thumbStart < maxStart;

  return (
    <div className="flex items-center gap-2">
      {photos.length > VISIBLE_THUMBS && (
        <button
          onClick={() => setActiveSlide(Math.max(0, activeSlide - 1))}
          className="p-1 rounded-full flex-shrink-0 transition-smooth"
          style={{
            background: canScrollLeft ? 'rgba(249,115,22,0.15)' : 'transparent',
            color: canScrollLeft ? '#F97316' : 'var(--fg-muted)',
            opacity: canScrollLeft ? 1 : 0.3,
            cursor: canScrollLeft ? 'pointer' : 'default',
          }}
          disabled={!canScrollLeft}
        >
          <ChevronLeft size={16} />
        </button>
      )}
      <div className="flex gap-2 flex-1">
        {visiblePhotos.map((src, vi) => {
          const i = thumbStart + vi;
          return (
            <button
              key={i}
              onClick={() => setActiveSlide(i)}
              className="flex-1 aspect-square rounded-lg overflow-hidden transition-smooth"
              style={{
                border: i === activeSlide ? '2px solid #F97316' : '1px solid rgba(249,115,22,0.1)',
                opacity: i === activeSlide ? 1 : 0.35,
                filter: i === activeSlide ? 'none' : 'brightness(0.6)',
              }}
            >
              <img src={src} alt={`${itemName} thumbnail ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          );
        })}
      </div>
      {photos.length > VISIBLE_THUMBS && (
        <button
          onClick={() => setActiveSlide(Math.min(photos.length - 1, activeSlide + 1))}
          className="p-1 rounded-full flex-shrink-0 transition-smooth"
          style={{
            background: canScrollRight ? 'rgba(249,115,22,0.15)' : 'transparent',
            color: canScrollRight ? '#F97316' : 'var(--fg-muted)',
            opacity: canScrollRight ? 1 : 0.3,
            cursor: canScrollRight ? 'pointer' : 'default',
          }}
          disabled={!canScrollRight}
        >
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  );
};

function extractDescription(raw) {
  const marker = '#DESCRIPTION';
  const idx = raw.indexOf(marker);
  if (idx === -1) return raw;
  return raw.substring(idx + marker.length).trim();
}

const DetailSection = ({ detailsFile, detailsMd, setDetailsMd }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!detailsFile) { setLoading(false); return; }
    setLoading(true);
    fetch(detailsFile)
      .then((res) => {
        if (!res.ok) throw new Error('Not found');
        return res.text();
      })
      .then((text) => setDetailsMd(extractDescription(text)))
      .catch(() => setDetailsMd(''))
      .finally(() => setLoading(false));
  }, [detailsFile, setDetailsMd]);

  if (loading || !detailsMd) return null;

  return (
    <div className="mt-12 pt-8 border-t border-subtle">
      <div className="prose prose-invert max-w-none product-details">
        <ReactMarkdown
          components={{
            h1: ({ children }) => <h2 className="text-2xl font-bold text-foreground-primary mt-8 mb-4 first:mt-0">{children}</h2>,
            h2: ({ children }) => <h3 className="text-xl font-semibold text-foreground-primary mt-6 mb-3">{children}</h3>,
            h3: ({ children }) => <h4 className="text-lg font-semibold text-foreground-primary mt-5 mb-2">{children}</h4>,
            p: ({ children }) => <p className="text-foreground-secondary leading-relaxed mb-4">{children}</p>,
            ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-4 text-foreground-secondary">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 mb-4 text-foreground-secondary">{children}</ol>,
            li: ({ children }) => <li className="text-foreground-secondary">{children}</li>,
            table: ({ children }) => (
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm border-collapse">{children}</table>
              </div>
            ),
            thead: ({ children }) => <thead className="border-b border-subtle">{children}</thead>,
            th: ({ children }) => <th className="text-left py-2 px-3 text-foreground-muted font-semibold text-xs uppercase tracking-wider">{children}</th>,
            td: ({ children }) => <td className="py-2 px-3 text-foreground-secondary border-b border-subtle/50">{children}</td>,
            code: ({ inline, children }) =>
              inline
                ? <code className="text-sm px-1.5 py-0.5 rounded" style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316' }}>{children}</code>
                : <pre className="rounded-lg p-4 mb-4 overflow-x-auto text-sm text-foreground-secondary" style={{ background: 'rgba(0,0,0,0.3)' }}><code>{children}</code></pre>,
            pre: ({ children }) => <>{children}</>,
            a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="transition-smooth hover:underline" style={{ color: '#F97316' }}>{children}</a>,
            strong: ({ children }) => <strong className="text-foreground-primary font-semibold">{children}</strong>,
            hr: () => <hr className="border-subtle my-6" />,
          }}
        >
          {detailsMd}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default ProductDetail;
